import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, CalendarCheck } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { SERVICES, BUDGET_OPTIONS } from "@/data/content";

export const openLeadModal = (service = "") =>
  window.dispatchEvent(new CustomEvent("open-lead-modal", { detail: { service } }));

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", phone: "", email: "", company: "", service: "", budget: "", city: "", message: "" };

const LeadModal = () => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      setForm((f) => ({ ...f, service: e.detail?.service || "" }));
      setOpen(true);
    };
    window.addEventListener("open-lead-modal", handler);
    return () => window.removeEventListener("open-lead-modal", handler);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { ...form, email: form.email || null, source: "strategy-call" });
      toast.success("You're on the list! Our team will reach out within 2 to 4 hours. Check your inbox!");
      setForm(initial);
      setOpen(false);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try WhatsApp instead.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-indigo-400/60 focus:ring-1 focus:ring-indigo-400/40 transition-colors";

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[90] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
          onClick={() => setOpen(false)}
          data-testid="lead-modal-overlay"
        >
          <motion.div
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="glow-border-strong rounded-t-3xl sm:rounded-3xl w-full max-w-lg max-h-[92vh] overflow-y-auto p-6 sm:p-8"
            data-testid="lead-modal"
          >
            <div className="flex items-start justify-between mb-6">
              <div>
                <h3 className="font-display text-2xl font-bold text-white tracking-tight flex items-center gap-2">
                  <CalendarCheck className="w-6 h-6 text-indigo-400" /> Book Your Free Strategy Call
                </h3>
                <p className="mt-2 text-sm text-zinc-400">Tell us about your brand — we'll call you within 24 hours.</p>
              </div>
              <button data-testid="lead-modal-close-btn" onClick={() => setOpen(false)} className="text-zinc-500 hover:text-white transition-colors p-1">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={submit} className="space-y-4" data-testid="lead-capture-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input required placeholder="Full Name *" value={form.name} onChange={set("name")} className={inputCls} data-testid="lead-form-name-input" />
                <input required placeholder="Phone / WhatsApp *" value={form.phone} onChange={set("phone")} className={inputCls} data-testid="lead-form-phone-input" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input type="email" placeholder="Email" value={form.email} onChange={set("email")} className={inputCls} data-testid="lead-form-email-input" />
                <input placeholder="Company / Brand" value={form.company} onChange={set("company")} className={inputCls} data-testid="lead-form-company-input" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <select value={form.service} onChange={set("service")} className={`${inputCls} appearance-none`} data-testid="lead-form-service-select">
                  <option value="" className="bg-zinc-900">Service of interest</option>
                  {SERVICES.map((s) => (
                    <option key={s.title} value={s.title} className="bg-zinc-900">{s.title}</option>
                  ))}
                  {form.service && !SERVICES.some((s) => s.title === form.service) && (
                    <option value={form.service} className="bg-zinc-900">{form.service}</option>
                  )}
                </select>
                <select value={form.budget} onChange={set("budget")} className={`${inputCls} appearance-none`} data-testid="lead-form-budget-select">
                  <option value="" className="bg-zinc-900">Budget range</option>
                  {BUDGET_OPTIONS.map((b) => (
                    <option key={b} value={b} className="bg-zinc-900">{b}</option>
                  ))}
                </select>
              </div>
              <input placeholder="City" value={form.city} onChange={set("city")} className={inputCls} data-testid="lead-form-city-input" />
              <textarea placeholder="Tell us about your campaign goals..." rows={3} value={form.message} onChange={set("message")} className={`${inputCls} resize-none`} data-testid="lead-form-message-input" />
              <button
                type="submit"
                disabled={loading}
                data-testid="lead-form-submit-btn"
                className="btn-gradient w-full text-white font-semibold py-3.5 rounded-full inline-flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                {loading ? "Booking..." : "Book My Free Strategy Call"}
              </button>
              <p className="text-center text-[11px] text-zinc-500">100% free consultation. No spam, ever.</p>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LeadModal;
